/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CMS.ASSET.LIBRARY — mobile-native.
 *
 * Touch-first counterpart of src/pages/cms/CmsAssetLibraryPage.tsx. A DISTINCT
 * presentation (dense card list + bottom-sheet metadata detail) over the SAME
 * data layer: reads via apiDb (cms_asset + cms_asset_type/_tl for the localized
 * type label). §16: never the desktop grid re-hosted.
 *
 * Read-only on mobile by design — uploading, editing alt text and deleting stay
 * on the desktop screen (they need the Storage bucket + Common Routine write
 * path). This screen surfaces the library and each asset's metadata for review,
 * so it imports no write helpers.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Inbox, X, ExternalLink,
  Image as ImageIcon, FileText, Video, Music, File as FileIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow,
  MobileFilterChips, MobileStatusPill,
  type MobileLeadTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import type { CmsAssetRow, CmsAssetTypeRow } from '../../types/cms';

type StatusFacet = 'all' | 'active' | 'inactive';

interface TlRow { language_id: string; display_text: string }

/** Presentational lead-icon treatment per asset type (tone + glyph). */
const TYPE_LEAD: Record<string, { tone: MobileLeadTone; Icon: LucideIcon }> = {
  image:    { tone: 'blue',  Icon: ImageIcon },
  video:    { tone: 'amber', Icon: Video },
  audio:    { tone: 'green', Icon: Music },
  document: { tone: 'slate', Icon: FileText },
};

function leadFor(assetTypeCode: string): { tone: MobileLeadTone; Icon: LucideIcon } {
  return TYPE_LEAD[assetTypeCode] ?? { tone: 'slate', Icon: FileIcon };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MobileCmsAssetLibraryPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();
  const [assets, setAssets] = useState<CmsAssetRow[]>([]);
  const [assetTypes, setAssetTypes] = useState<CmsAssetTypeRow[]>([]);
  // §17.B4: asset_type_code -> localized display_text (from cms_asset_type_tl).
  const [typeLabels, setTypeLabels] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [typeFacet, setTypeFacet] = useState<string>('all');
  const [selected, setSelected] = useState<CmsAssetRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawAssets, error: aErr } = await apiDb
        .from('cms_asset')
        .select('*')
        .order('created_at', { ascending: false });
      if (aErr) throw aErr;
      setAssets((rawAssets ?? []) as CmsAssetRow[]);

      // Resolve asset-type codes to translated names for display (§17.B4 — never
      // the bare code). CMS _tl tables key on language_id (§8), not locale_code.
      const { data: rawTypes, error: tErr } = await apiDb
        .from('cms_asset_type')
        .select('asset_type_code, sort_order, is_active, cms_asset_type_tl ( language_id, display_text )')
        .order('sort_order');
      if (tErr) throw tErr;

      type RawType = CmsAssetTypeRow & { cms_asset_type_tl?: TlRow[] };
      const list = ((rawTypes ?? []) as RawType[]).map((r) => {
        const tls = r.cms_asset_type_tl ?? [];
        const exact = tls.find((tl) => tl.language_id === activeLocale);
        const fb = tls.find((tl) => tl.language_id === 'EN_US') ?? tls[0];
        return { ...r, display_text: exact?.display_text ?? fb?.display_text ?? r.asset_type_code };
      });
      setAssetTypes(list);
      const labels: Record<string, string> = {};
      for (const r of list) labels[r.asset_type_code] = r.display_text ?? r.asset_type_code;
      setTypeLabels(labels);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (type labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const typeLabel = useCallback((code: string) => typeLabels[code] ?? code, [typeLabels]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return assets.filter((a) => {
      if (statusFacet === 'active' && a.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && a.is_active !== 'N') return false;
      if (typeFacet !== 'all' && a.asset_type_code !== typeFacet) return false;
      if (!q) return true;
      return (
        a.original_filename.toLowerCase().includes(q) ||
        a.internal_code.toLowerCase().includes(q) ||
        typeLabel(a.asset_type_code).toLowerCase().includes(q)
      );
    });
  }, [assets, filter, statusFacet, typeFacet, typeLabel]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.assets.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const dimensions = (a: CmsAssetRow) =>
    a.width_px && a.height_px ? `${a.width_px} × ${a.height_px} px` : null;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.cms_assets.title', 'Asset Library')}
        description={t('mobile.cms_assets.description', 'Images, video, audio and documents managed for content pages.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.assets.search', 'Search assets')}
          aria-label={t('mobile.assets.search', 'Search assets')}
        />
      </div>

      {/* Status facet */}
      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {/* Type facet — one chip per asset type, labels resolved via _tl (§17.B4). */}
      <MobileFilterChips
        chips={[
          { value: 'all', label: t('mobile.assets.type_all', 'All types') },
          ...assetTypes.map((at) => ({ value: at.asset_type_code, label: typeLabel(at.asset_type_code) })),
        ]}
        isActive={(v) => typeFacet === v}
        onSelect={(v) => setTypeFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.assets.none', 'No assets match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((a) => {
            const lead = leadFor(a.asset_type_code);
            const LeadIcon = lead.Icon;
            return (
              <MobileCardRow
                key={a.asset_id}
                lead={<LeadIcon />}
                leadTone={lead.tone}
                title={a.original_filename}
                subtitle={`${typeLabel(a.asset_type_code)} · ${formatBytes(a.file_size_bytes)}`}
                trailing={
                  <MobileStatusPill tone={a.is_active === 'Y' ? 'ok' : 'no'}>
                    {a.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
                chevron
                onClick={() => setSelected(a)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet — asset metadata (read-only) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.assets.detail', 'Asset detail')}>
        <div className="m-grabber" />
        {selected && (() => {
          const lead = leadFor(selected.asset_type_code);
          const LeadIcon = lead.Icon;
          const dims = dimensions(selected);
          return (
            <>
              <div className="m-sheet-id">
                <span className="m-av"><LeadIcon size={20} /></span>
                <span>
                  <div className="m-nm">{selected.original_filename}</div>
                  <div className="m-em">{selected.internal_code}</div>
                </span>
              </div>
              <MobileContentCard>
                <MobileDetailRow
                  label={t('mobile.assets.status', 'Status')}
                  value={
                    <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                      {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                    </MobileStatusPill>
                  }
                />
                <MobileDetailRow label={t('mobile.assets.type', 'Type')} value={typeLabel(selected.asset_type_code)} />
                <MobileDetailRow label={t('mobile.assets.mime', 'MIME type')} value={selected.mime_type} />
                <MobileDetailRow label={t('mobile.assets.size', 'File size')} value={formatBytes(selected.file_size_bytes)} />
                {dims && (
                  <MobileDetailRow label={t('mobile.assets.dimensions', 'Dimensions')} value={dims} />
                )}
                {selected.duration_seconds != null && (
                  <MobileDetailRow
                    label={t('mobile.assets.duration', 'Duration')}
                    value={t('mobile.assets.seconds', '{{count}}s', { count: selected.duration_seconds })}
                  />
                )}
                <MobileDetailRow label={t('mobile.assets.alt_text', 'Alt text')} value={selected.alt_text_default ?? t('common.none', 'None')} />
                <MobileDetailRow label={t('mobile.assets.created', 'Created')} value={formatDateTime(selected.created_at)} />
                <MobileDetailRow label={t('mobile.assets.updated', 'Updated')} value={formatDateTime(selected.updated_at)} />
              </MobileContentCard>
              <div className="m-actionbar">
                <a
                  className="m-btn m-btn-primary"
                  href={selected.file_path}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={16} />
                  {t('mobile.assets.open_file', 'Open file')}
                </a>
                <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                  <X size={16} />
                </button>
              </div>
            </>
          );
        })()}
      </div>
    </section>
  );
}
